import { useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, AlertTriangle, Clock, FileText, Shield, LifeBuoy, Users, Settings, Bell, LogOut, ChevronLeft, ChevronRight, X, Network, Sparkles } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useAssistantStore } from '../../store/assistantStore';
import { useAssistantEnabled } from '../../hooks/useAssistantStatus';
import { tenantName, tenantLogo } from '../../config/tenant';
import {
  ADMIN_ROLES,
  OPS_ADMIN_ROLES,
  ROLES_WITH_HISTORIAN,
  ROLES_WITH_SECURITY,
  type Role,
} from '../../constants/roles';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

type NavItem = {
  path: string;
  label: string;
  icon: typeof LayoutDashboard;
  allowedRoles?: Role[];
};

const NAV_ITEMS: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/alarms', label: 'Alarms', icon: AlertTriangle },
  { path: '/historian', label: 'Historian', icon: Clock, allowedRoles: ROLES_WITH_HISTORIAN },
  { path: '/reports', label: 'Reports', icon: FileText, allowedRoles: ROLES_WITH_HISTORIAN },
  { path: '/security', label: 'Security', icon: Shield, allowedRoles: ROLES_WITH_SECURITY },
  { path: '/tickets', label: 'Support', icon: LifeBuoy },
  { path: '/settings/notifications', label: 'Notifications', icon: Bell },
];

const ADMIN_ITEMS = [
  { path: '/admin/users', label: 'Users', icon: Users },
  { path: '/admin/org-units', label: 'Org Units', icon: Network },
  { path: '/admin/settings', label: 'Settings', icon: Settings },
];

// Djin-internal-ops surfaces — narrower than ADMIN_ITEMS. Visible to
// platform_admin + parent_org_admin only (issue #265).
const OPS_ADMIN_ITEMS = [
  { path: '/admin/notifications/audit', label: 'Notification Audit', icon: Bell },
];

export function Sidebar({ collapsed, onToggle, mobileOpen = false, onMobileClose }: SidebarProps) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const location = useLocation();
  const openAssistant = useAssistantStore((s) => s.open);
  const assistantEnabled = useAssistantEnabled();
  const isAdmin = !!user && ADMIN_ROLES.includes(user.role as Role);
  const isOpsAdmin = !!user && OPS_ADMIN_ROLES.includes(user.role as Role);
  const visibleNavItems = NAV_ITEMS.filter(
    (item) => !item.allowedRoles || (user?.role && item.allowedRoles.includes(user.role as Role)),
  );

  const initials = user
    ? `${(user.first_name || user.email)[0]}${(user.last_name || '')[0] || ''}`.toUpperCase()
    : '?';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Auto-close mobile sidebar on route change
  useEffect(() => {
    if (mobileOpen && onMobileClose) {
      onMobileClose();
    }
    // Only trigger on pathname change, not when mobileOpen/onMobileClose change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
      isActive
        ? 'bg-ems-green/10 text-ems-green border-l-[3px] border-ems-green'
        : 'text-gray-600 hover:text-ems-green hover:bg-gray-100'
    }`;

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className={`flex items-center justify-center border-b border-gray-200 ${collapsed && !mobileOpen ? 'px-2 py-3' : 'px-4 py-5'}`}>
        <img src={tenantLogo} alt={tenantName} className={`flex-shrink-0 object-contain ${collapsed && !mobileOpen ? 'w-10 h-10' : 'w-36 h-36'}`} />
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {/* AI Assistant — opens the docked chat panel (djin-009) rather than navigating.
            Only for tenants with the assistant switched on (#860). */}
        {assistantEnabled && (
          <button
            type="button"
            onClick={openAssistant}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 text-gray-600 hover:text-ems-green hover:bg-gray-100"
            title={collapsed && !mobileOpen ? 'Assistant' : undefined}
          >
            <Sparkles className="w-5 h-5 flex-shrink-0" />
            {(!collapsed || mobileOpen) && <span>Assistant</span>}
          </button>
        )}

        {visibleNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end
            className={linkClass}
            title={collapsed && !mobileOpen ? item.label : undefined}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {(!collapsed || mobileOpen) && <span>{item.label}</span>}
          </NavLink>
        ))}

        {/* Admin section */}
        {isAdmin && (
          <>
            {(!collapsed || mobileOpen) && (
              <div className="pt-4 pb-1 px-3">
                <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Admin</span>
              </div>
            )}
            {ADMIN_ITEMS.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={linkClass}
                title={collapsed && !mobileOpen ? item.label : undefined}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {(!collapsed || mobileOpen) && <span>{item.label}</span>}
              </NavLink>
            ))}
          </>
        )}

        {/* Djin Ops section — internal-only, narrower than ADMIN section */}
        {isOpsAdmin && (
          <>
            {(!collapsed || mobileOpen) && (
              <div className="pt-4 pb-1 px-3">
                <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Djin Ops</span>
              </div>
            )}
            {OPS_ADMIN_ITEMS.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={linkClass}
                title={collapsed && !mobileOpen ? item.label : undefined}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {(!collapsed || mobileOpen) && <span>{item.label}</span>}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* Collapse toggle — desktop only */}
      <button
        onClick={onToggle}
        className="hidden md:flex items-center justify-center h-10 border-t border-gray-200 text-gray-400 hover:text-ems-green transition-colors"
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>

      {/* User footer */}
      <div className="px-3 py-3 border-t border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-ems-forest flex items-center justify-center text-ems-green text-xs font-semibold flex-shrink-0">
            {initials}
          </div>
          {(!collapsed || mobileOpen) && (
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-900 truncate">
                {user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user?.email}
              </p>
              <p className="text-xs text-gray-500 capitalize">{user?.role?.replace(/_/g, ' ')}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="text-gray-400 hover:text-ems-red transition-colors"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar — persistent column, hidden on mobile */}
      <aside
        className={`hidden md:flex flex-col bg-white border-r border-gray-200 h-screen sticky top-0 transition-all duration-200 ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        {sidebarContent}
      </aside>

      {/* Mobile sidebar — overlay with backdrop */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 animate-fade-in"
            onClick={onMobileClose}
            aria-hidden="true"
          />
          {/* Sidebar panel */}
          <aside className="absolute inset-y-0 left-0 w-60 bg-white border-r border-gray-200 flex flex-col animate-slide-in-left">
            {/* Close button */}
            <button
              onClick={onMobileClose}
              className="absolute top-3 right-3 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors z-10"
              aria-label="Close menu"
            >
              <X className="w-4 h-4" />
            </button>
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
}
